import React, { useMemo, useEffect, useState } from "react";
import objectPath from "object-path";
import { Outlet } from "react-router-dom";
// LayoutContext
import { useHtmlClassService } from "../_core/MetronicLayout";
// Import Layout components
import { HeaderMobile } from "./header-mobile/HeaderMobile";
import { AdminAside } from "./aside/AdminAside";
import { Footer } from "./footer/Footer";
import { LayoutInit } from "./LayoutInit";
import { SubHeader } from "./subheader/SubHeader";
import PaymentGraceBanner from "../../../app/components/Billing/PaymentGraceBanner";
import { ScrollTop } from "./extras/ScrollTop";
import { LocalStorageService } from "../../../app/base/services/local-storage.service";

// Whether the sidebar is collapsed to icons. Kept across page loads; logout
// clears it (AuthService.logout).
const ASIDE_MINIMIZED_KEY = "asideMinimized";

export function AdminLayout({ children }) {
   const uiService = useHtmlClassService();
   const layoutProps = useMemo(() => {
      return {
         selfLayout: objectPath.get(uiService.config, "self.layout"),
         subheaderDisplay: objectPath.get(uiService.config, "subheader.display"),
         contentCssClasses: uiService.getClasses("content", true),
         contentContainerClasses: uiService.getClasses("content_container", true),
         contentExtended: objectPath.get(uiService.config, "content.extended")
      };
   }, [uiService]);

   const [isMinimized, setIsMinimized] = useState(
      () => LocalStorageService.getItem(ASIDE_MINIMIZED_KEY) === true
   );

   useEffect(() => {
      document.body.classList.remove("aside-minimize");
   }, []);

   const checkIsMenuMinimized = (data) => {
      LocalStorageService.setItem(ASIDE_MINIMIZED_KEY, data);
      setIsMinimized(data);
      // The page gets wider or narrower without the window resizing. Once the
      // sidebar's 0.3s transition is over, let whatever measures itself on
      // window resize (charts, scroll arrows) measure again.
      setTimeout(() => window.dispatchEvent(new Event("resize")), 350);
   };

   const validPaths = [
      "/admin-payment-plans",
      "/tasks",
      "/relationships",
      "/quick-actions"
      // "/task-management"
   ];

   const isMatchingPath = validPaths.some((path) => window.location.pathname.startsWith(path));
   // Pages whose content spans the whole width instead of a centered
   // `container`. The top bar and footer then span it too, so they stay in
   // line with the content however wide the window or the sidebar is.
   const isFullWidthPage =
      window.location.pathname.startsWith("/survey-analytics") ||
      window.location.pathname.includes("audience") ||
      window.location.pathname.includes("assignments") ||
      window.location.pathname.includes("automated-campaigns") ||
      window.location.pathname.includes("surveys") ||
      isMatchingPath;
   return layoutProps.selfLayout !== "blank" ? (
      <>
         {/*begin::Main*/}
         <HeaderMobile />

         <div className="d-flex flex-column flex-root">
            {/*begin::Page*/}
            <div className="d-flex flex-row flex-column-fluid page">
               <AdminAside isMinimized={isMinimized} onMenuMinimize={checkIsMenuMinimized} />

               {/*begin::Wrapper*/}
               <div
                  className={`d-flex flex-column flex-row-fluid wrapper ${
                     isMinimized ? "minimized" : ""
                  }`}
                  // className="w-100"
                  id="kt_wrapper"
               >
                  {/*begin::Content*/}
                  <div
                     id="kt_content"
                     className={`content ${layoutProps.contentCssClasses} d-flex flex-column flex-column-fluid`}
                  >
                     {layoutProps.subheaderDisplay && <SubHeader fluid={isFullWidthPage} />}
                     {/* A payment owed inside its grace period. */}
                     <PaymentGraceBanner />

                     {/*begin::Entry*/}
                     {layoutProps.contentExtended && <>{children}</>}

                     {!layoutProps.contentExtended && (
                        <div
                           className="d-flex flex-column-fluid justify-content-center"
                           style={{
                              backgroundColor: isMatchingPath
                                 ? "#252938"
                                 : window.location.pathname.startsWith("/survey-analytics") ||
                                   window.location.pathname.includes("audience") ||
                                   window.location.pathname.includes("assignments") ||
                                   window.location.pathname.includes("automated-campaigns") ||
                                   window.location.pathname.includes("surveys") ||
                                   window.location.pathname.includes("/balance")
                                 ? "#fff"
                                 : "#eef2ff"
                           }}
                        >
                           {/*begin::Container*/}
                           <div
                              className={
                                 isFullWidthPage ? "w-100" : layoutProps.contentContainerClasses
                              }
                              style={
                                 window.location.pathname.startsWith("/survey/test/") ||
                                 window.location.pathname.startsWith("/survey/submission/") ||
                                 window.location.pathname.startsWith("/deleted-items") ||
                                 window.location.pathname.startsWith("/survey/edit/") ||
                                 window.location.pathname.startsWith("/survey/highlights/")
                                    ? { padding: "0" }
                                    : window.location.pathname.startsWith("/dashboard") ||
                                      window.location.pathname.startsWith("/survey-analytics") ||
                                      window.location.pathname.startsWith("/survey/data")
                                    ? { padding: "0", backgroundColor: "#EEF2FF" }
                                    : null
                              }
                           >
                              <Outlet />
                           </div>
                           {/*end::Container*/}
                        </div>
                     )}

                     {/*end::Entry*/}
                  </div>
                  {/*end::Content*/}
                  <Footer fluid={isFullWidthPage} />
               </div>
               {/*end::Wrapper*/}
            </div>
            {/*end::Page*/}
         </div>

         <ScrollTop />

         {/*end::Main*/}
         <LayoutInit />
      </>
   ) : (
      // BLANK LAYOUT
      <div className="d-flex flex-column flex-root">{children}</div>
   );
}
